import {
  CruiseSaleLiteModel,
  CruiseSaleModel,
  getCruiseSaleKey,
  getCruiseSalesKey,
  useGetCruiseSale,
  useGetCruiseSales,
} from 'api-hooks/sale/cruise';
import { OnChangeParams, Option } from 'baseui/select';
import { AuthorizationRules, NavigationRoutes } from 'common/constants';
import { SaleStatusEnum } from 'common/constants/enum';
import { QueryTransformer } from 'common/helpers/common';
import { transformLabel } from 'common/utils/string';
import { Input } from 'components/elements';
import { useAuthorization } from 'hooks/use-authorization';
import useNavigation from 'hooks/use-navigation';
import useSelectInputHelper from 'hooks/use-select-input-helper';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

function transformer(
  item: CruiseSaleLiteModel | CruiseSaleModel,
  type?: 'default' | 'invoice',
): Option {
  return {
    id: item.id,
    label:
      type === 'invoice'
        ? transformLabel([item.invoiceNumber, item.customer.name])
        : item.code,
  };
}

interface Props {
  name: string;
  required?: boolean;
  label?: string;
  placeholder?: string;
  onClose?: () => void;
  onAfterSelectItem?: (prevValue?: any, value?: any) => void;
  onAfterChange?: (value?: OnChangeParams) => void;
  onAfterDetailChange?: (value: CruiseSaleModel) => void;
  noMargin?: boolean;
  disabled?: boolean;
  type?: 'default' | 'invoice';
  isSales?: boolean;
}

export default function CruiseSaleSelectInput(props: Props) {
  const { t } = useTranslation();
  const {
    label = t('common:cruise_sales'),
    placeholder = t('common:select_cruise_sales'),
  } = props;
  const { control, setValue } = useFormContext<any>();
  const value = useWatch({ control, name: props.name });

  const { navigate } = useNavigation();

  const { can } = useAuthorization();

  const { overrides, ...selectProps } = useSelectInputHelper({
    value,
    onSelectItem: (_value) => {
      setValue(props.name, _value[0]?.id, {
        shouldTouch: true,
        shouldValidate: true,
      });
      props.onAfterSelectItem?.(value, _value[0]?.id);
    },
    useListQueryHook: useGetCruiseSales,
    useDetailLazyQueryHook: useGetCruiseSale,
    listKeyFunction: getCruiseSalesKey,
    detailKeyFunction: getCruiseSaleKey,
    getMemoizedListVariables: React.useCallback(
      (page, q) => ({
        params: {
          q,
          page,
          ...(props.type === 'invoice' && {
            filter: {
              status: props.isSales
                ? `${SaleStatusEnum.Active}`
                : `${SaleStatusEnum.Active}|${SaleStatusEnum.OpenTicket}|${SaleStatusEnum.Completed}|${SaleStatusEnum.Waiting}`,

              is_completed: false,
            },
            selection: 'on',
          }),
        },
      }),
      [props.isSales, props.type],
    ),
    renderCreate:
      props.type === 'invoice'
        ? false
        : !!can(AuthorizationRules.CruiseSalesCreate),
    onClickCreate: React.useCallback(() => {
      navigate(NavigationRoutes.CruiseSalesCreate);
      props.onClose?.();
    }, [navigate, props]),
    getMemoizedDetailVariables: React.useCallback(
      (value: any) => ({
        id:
          typeof value === 'object'
            ? Array.isArray(value)
              ? value?.[0]?.id
              : value?.id
            : value,
      }),
      [],
    ),
    listTransformer: React.useCallback(
      (result) => {
        return result.data.map((v) => transformer(v, props.type));
      },
      [props.type],
    ),
    detailTransformer: React.useCallback((result) => {
      const newResult = QueryTransformer(result, CruiseSaleModel);
      props.onAfterDetailChange?.(newResult.data);
      return transformer(newResult.data, props.type);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []),
    paginationTransformer: React.useCallback((result) => {
      return result.meta;
    }, []),
  });

  return (
    <Input
      type="select"
      name={props.name}
      required={props.required}
      label={label}
      placeholder={placeholder}
      onAfterChange={props.onAfterChange}
      noMargin={props.noMargin}
      disabled={props.disabled}
      overrides={overrides}
      {...selectProps}
    />
  );
}
